import React from "react";
import { AVATAR_HERO } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Crown, LogOut, Sparkles, XCircle } from "lucide-react";
import { cancelSubscription } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const links = [
  { label: "Ask", href: "#ask" },
  { label: "Wisdom Wall", href: "#wall" },
  { label: "Content Studio", href: "#studio" },
  { label: "Sessions", href: "#sessions" },
  { label: "About", href: "#about" },
];

export const Navbar = () => {
  const { user, loading, setAuthOpen, setUpgradeOpen, logout, billingLive, checkAuth } = useAuth();

  const handleCancel = async () => {
    try {
      const res = await cancelSubscription();
      toast.success(res.message || "Auto-renewal cancelled.");
      checkAuth && checkAuth();
    } catch {
      toast.error("Could not cancel. Please try again.");
    }
  };

  const badge = () => {
    if (!billingLive) {
      return (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-leaf bg-leaf/10 rounded-full px-3 py-1" data-testid="beta-badge">
          <Sparkles size={12} /> Beta · Free
        </span>
      );
    }
    if (!user) return null;
    if (user.status === "premium")
      return (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-ink bg-ochre rounded-full px-3 py-1" data-testid="premium-badge">
          <Crown size={12} /> Premium
        </span>
      );
    if (user.status === "trial")
      return (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-clay bg-clay/10 rounded-full px-3 py-1" data-testid="trial-badge">
          <Sparkles size={12} /> Trial · {user.trial_days_left}d left
        </span>
      );
    return (
      <button
        onClick={() => setUpgradeOpen(true)}
        className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-clay hover:underline"
        data-testid="upgrade-link"
      >
        <Crown size={12} /> Go Premium
      </button>
    );
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-sand/70 border-b border-border/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-logo">
          <img src={AVATAR_HERO} alt="Shabba Robo" className="w-10 h-10 rounded-full object-cover ring-2 ring-clay/40" />
          <div className="leading-tight">
            <span className="font-display font-black text-lg text-ink">Shabba</span>
            <span className="font-display text-lg text-clay"> Robo</span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-semibold text-ink/70 hover:text-clay transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {badge()}
          {loading ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-border bg-sand-light pl-1 pr-3 py-1 hover:bg-sand-dark/30 transition-colors" data-testid="user-menu-trigger">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-clay text-white font-bold flex items-center justify-center text-sm">
                      {(user.name || user.email || "?")[0].toUpperCase()}
                    </span>
                  )}
                  <span className="hidden sm:block text-sm font-semibold text-ink max-w-[120px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-sand-light border-border">
                <DropdownMenuLabel className="text-ink/60 font-normal text-xs truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.status !== "premium" && billingLive && (
                  <DropdownMenuItem onClick={() => setUpgradeOpen(true)} className="cursor-pointer" data-testid="menu-upgrade">
                    <Crown size={16} className="mr-2 text-ochre" /> Go Premium
                  </DropdownMenuItem>
                )}
                {user.status === "premium" && billingLive && !user.subscription_cancelled && (
                  <DropdownMenuItem onClick={handleCancel} className="cursor-pointer" data-testid="menu-cancel-sub">
                    <XCircle size={16} className="mr-2 text-ink/60" /> Cancel auto-renew
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-clay" data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => setAuthOpen(true)}
              className="rounded-full bg-clay text-white text-sm font-bold px-5 py-2.5 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(212,93,60,0.18)]"
              data-testid="nav-signin"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
